import { useCallback, useEffect, useRef } from "react";

interface UseDebouncedCallbackOptions {
  wait: number;
}

export function useDebouncedCallback<T extends (...args: never[]) => void>(
  callback: T,
  options: UseDebouncedCallbackOptions,
): T & { cancel: () => void; flush: () => void } {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Always keep the latest callback
  callbackRef.current = callback;

  const cancel = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const flush = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      callbackRef.current();
    }
  }, []);

  // Cancel on unmount
  useEffect(() => cancel, [cancel]);

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      cancel();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        callbackRef.current(...args);
      }, options.wait);
    },
    [cancel, options.wait],
  ) as T & { cancel: () => void; flush: () => void };

  debounced.cancel = cancel;
  debounced.flush = flush;

  return debounced;
}
